import { nextTick, onUnmounted, ref } from 'vue'

import { usePitcherApi, PitcherEventName, type PitcherEvent } from '@sdk/main'
import {
  PeerConnectivityActions,
  registerPeerConnectivityHandler,
  type PeerConnectivityEvent,
} from '@sdk/peer-connectivity'
import useCanvas from '@canvas-builder/composables/useCanvas'

export const usePitcherEvents = (
  goToNextPage?: () => void,
  goToPreviousPage?: () => void,
  onCanvasCloseRequested?: () => void,
) => {
  const pitcherAPI = usePitcherApi()
  const isOpenPhotosCaptured = ref(false)
  const isCanvasPageNextListenerRegistered = ref(false)
  const isCanvasPagePreviousListenerRegistered = ref(false)
  const isCanvasCloseListenerRegistered = ref(false)
  const { activeCanvas, pitcherSettings } = useCanvas()
  const unregisterPeerConnectivityHandlers: Array<() => void> = []

  const notifyUserAboutPhotosCaptured = () => {
    isOpenPhotosCaptured.value = true
  }

  const handleCanvasNavigationRequested = (event: PitcherEvent, navigationFn: (() => void) | undefined) => {
    if (!navigationFn) return

    const eventData = event as { canvas_id?: string; current_page_index?: number }
    const canvasId = eventData?.canvas_id
    if (canvasId && activeCanvas.value?.id === canvasId) {
      navigationFn()
    }
  }

  const handleCanvasPageNextRequested = (event: PitcherEvent) => {
    handleCanvasNavigationRequested(event, goToNextPage)
  }

  const handleCanvasPagePreviousRequested = (event: PitcherEvent) => {
    handleCanvasNavigationRequested(event, goToPreviousPage)
  }

  const handleCanvasCloseRequested = (event: PitcherEvent) => {
    if (!onCanvasCloseRequested) return

    const eventData = event as { canvas_id?: string }
    const canvasId = eventData?.canvas_id
    if (canvasId && activeCanvas.value?.id === canvasId) {
      onCanvasCloseRequested()
    }
  }

  const registerPhotosCapturedListener = () => {
    pitcherAPI.on(PitcherEventName.PHOTOS_CAPTURED, notifyUserAboutPhotosCaptured)
  }

  const unregisterPhotosCapturedListener = () => {
    pitcherAPI.off(PitcherEventName.PHOTOS_CAPTURED, notifyUserAboutPhotosCaptured)
  }

  const registerCanvasPageNextListener = () => {
    if (isCanvasPageNextListenerRegistered.value || !goToNextPage) return
    pitcherAPI.on(PitcherEventName.CANVAS_PAGE_NEXT_REQUESTED, handleCanvasPageNextRequested)
    isCanvasPageNextListenerRegistered.value = true
  }

  const unregisterCanvasPageNextListener = () => {
    if (!isCanvasPageNextListenerRegistered.value) return
    pitcherAPI.off(PitcherEventName.CANVAS_PAGE_NEXT_REQUESTED, handleCanvasPageNextRequested)
    isCanvasPageNextListenerRegistered.value = false
  }

  const registerCanvasPagePreviousListener = () => {
    if (isCanvasPagePreviousListenerRegistered.value || !goToPreviousPage) return
    pitcherAPI.on(PitcherEventName.CANVAS_PAGE_PREVIOUS_REQUESTED, handleCanvasPagePreviousRequested)
    isCanvasPagePreviousListenerRegistered.value = true
  }

  const unregisterCanvasPagePreviousListener = () => {
    if (!isCanvasPagePreviousListenerRegistered.value) return
    pitcherAPI.off(PitcherEventName.CANVAS_PAGE_PREVIOUS_REQUESTED, handleCanvasPagePreviousRequested)
    isCanvasPagePreviousListenerRegistered.value = false
  }

  const registerCanvasCloseListener = () => {
    if (isCanvasCloseListenerRegistered.value || !onCanvasCloseRequested) return
    pitcherAPI.on(PitcherEventName.CANVAS_CLOSE_REQUESTED, handleCanvasCloseRequested)
    isCanvasCloseListenerRegistered.value = true
  }

  const unregisterCanvasCloseListener = () => {
    if (!isCanvasCloseListenerRegistered.value) return
    pitcherAPI.off(PitcherEventName.CANVAS_CLOSE_REQUESTED, handleCanvasCloseRequested)
    isCanvasCloseListenerRegistered.value = false
  }

  let legacyHandlersActive = false

  const registerLegacyListeners = () => {
    if (legacyHandlersActive) return
    registerCanvasPageNextListener()
    registerCanvasPagePreviousListener()
    registerCanvasCloseListener()
    legacyHandlersActive = true
  }

  const unregisterLegacyListeners = () => {
    if (!legacyHandlersActive) return
    unregisterCanvasPageNextListener()
    unregisterCanvasPagePreviousListener()
    unregisterCanvasCloseListener()
    legacyHandlersActive = false
  }

  const handlePeerEventHandshake = () => {
    if (!legacyHandlersActive) {
      return
    }
    unregisterLegacyListeners()
  }

  function registerPeerConnectivityHandlers() {
    const unsubscribeNavigation = registerPeerConnectivityHandler(
      [PeerConnectivityActions.CANVAS_PAGE_NEXT, PeerConnectivityActions.CANVAS_PAGE_PREVIOUS],
      handlePeerConnectivityNavigation,
    )
    unregisterPeerConnectivityHandlers.push(unsubscribeNavigation)

    if (onCanvasCloseRequested) {
      const unsubscribeClose = registerPeerConnectivityHandler(
        PeerConnectivityActions.CANVAS_CLOSE,
        handlePeerConnectivityCloseRequested,
      )
      unregisterPeerConnectivityHandlers.push(unsubscribeClose)
    }
  }

  const unregisterPeerConnectivityHandlersSafely = () => {
    while (unregisterPeerConnectivityHandlers.length) {
      const unsubscribe = unregisterPeerConnectivityHandlers.pop()
      unsubscribe?.()
    }
  }

  function doesEventTargetActiveCanvas(event: PeerConnectivityEvent): boolean {
    const targetCanvasId = event.context?.canvasId
    if (!targetCanvasId) {
      console.warn('[peer-connectivity] Ignoring event without canvasId')
      return false
    }

    return targetCanvasId === activeCanvas.value?.id
  }

  function handlePeerConnectivityNavigation(event: PeerConnectivityEvent) {
    handlePeerEventHandshake()
    if (!doesEventTargetActiveCanvas(event)) {
      return
    }

    if (event.action === PeerConnectivityActions.CANVAS_PAGE_NEXT) {
      goToNextPage?.()
      return
    }

    if (event.action === PeerConnectivityActions.CANVAS_PAGE_PREVIOUS) {
      goToPreviousPage?.()
    }
  }

  function handlePeerConnectivityCloseRequested(event: PeerConnectivityEvent) {
    handlePeerEventHandshake()
    if (!onCanvasCloseRequested) {
      return
    }
    if (!doesEventTargetActiveCanvas(event)) {
      return
    }

    onCanvasCloseRequested()
  }

  // Check if canvas navigation listeners should be registered (iOS only + feature flag check)
  const shouldRegisterPeerConnectivityListeners = async () => {
    try {
      const env = await pitcherAPI.getEnv()

      // Only register on iOS platform
      if (env.mode !== 'IOS') {
        return false
      }

      const enableMultipeerConnectivity = pitcherSettings.value.enable_multipeer_connectivity ?? false

      if (!enableMultipeerConnectivity) {
        return false
      }

      return true
    } catch (error) {
      console.error('Failed to check canvas navigation listener requirements:', error)
      return false
    }
  }

  if (goToNextPage || goToPreviousPage || onCanvasCloseRequested) {
    nextTick(async () => {
      const shouldRegister = await shouldRegisterPeerConnectivityListeners()

      if (shouldRegister) {
        registerLegacyListeners()
        registerPeerConnectivityHandlers()
      }
    })
  }

  onUnmounted(() => {
    unregisterPhotosCapturedListener()
    unregisterLegacyListeners()
    unregisterPeerConnectivityHandlersSafely()
  })

  return {
    isOpenPhotosCaptured,
    registerPhotosCapturedListener,
    unregisterPhotosCapturedListener,
  }
}
